import { replaceUrlWithoutRoutePublish } from "../../../../../../../_shared/_logic/routes-root-silent-url-replace"
import {
  coerceCalendarDateRangeInput,
  createDefaultCalendarDateRange,
  type CalendarDateRange,
} from "../_shared/_logic/calendar-date-range"

const normalizeId = (value: string | null | undefined) =>
  (value ?? "kehrblatt-calendar").trim().replace(/[^a-zA-Z0-9_-]+/g, "_") || "kehrblatt-calendar"

const stateId = (organizationId: string | null | undefined) =>
  normalizeId(organizationId ? `kehrblatt-calendar-${organizationId}` : "kehrblatt-calendar")

const key = (organizationId: string | null | undefined, name: string) =>
  `table_${stateId(organizationId)}_filter_${name}`

const formatDate = (value: Date | null | undefined) => {
  if (!value) return ""

  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const parseDate = (value: string | null) => {
  if (!value) return null

  const parsed = new Date(`${value}T12:00:00`)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const parseJsonStringArray = (value: string | null): string[] => {
  if (!value) return []

  try {
    const parsed = JSON.parse(value)

    if (Array.isArray(parsed)) {
      return parsed.map((entry) => `${entry ?? ""}`.trim()).filter(Boolean)
    }
  } catch {
    // Fall through to comma-separated legacy/fallback parsing.
  }

  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

const normalizeEmployeeIds = (value: string | string[] | null | undefined) => {
  const list = Array.isArray(value) ? value : value ? [value] : []

  return list.map((entry) => `${entry ?? ""}`.trim()).filter(Boolean)
}

const replaceUrl = (params: URLSearchParams) => {
  if (typeof window === "undefined") return

  const nextSearch = params.toString()
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash ?? ""}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash ?? ""}`

  if (nextUrl !== currentUrl) {
    replaceUrlWithoutRoutePublish(nextUrl)
  }
}

export const readCalendarUrlEmployeeIds = (organizationId: string | null | undefined): string[] => {
  if (typeof window === "undefined") return []

  const params = new URLSearchParams(window.location.search || "")

  return parseJsonStringArray(params.get(key(organizationId, "employees")))
}

export const readCalendarUrlDateRange = (
  organizationId: string | null | undefined,
): CalendarDateRange => {
  if (typeof window === "undefined") return createDefaultCalendarDateRange()

  const params = new URLSearchParams(window.location.search || "")
  const start = parseDate(params.get(key(organizationId, "start")))
  const end = parseDate(params.get(key(organizationId, "end")))

  if (!start && !end) return createDefaultCalendarDateRange()

  return coerceCalendarDateRangeInput({ start, end })
}

export const writeCalendarUrlDateRange = (
  organizationId: string | null | undefined,
  range: CalendarDateRange,
) => {
  if (typeof window === "undefined") return

  const defaults = createDefaultCalendarDateRange()
  const params = new URLSearchParams(window.location.search || "")
  const start = formatDate(range.start)
  const end = formatDate(range.end)
  const defaultStart = formatDate(defaults.start)
  const defaultEnd = formatDate(defaults.end)
  const startKey = key(organizationId, "start")
  const endKey = key(organizationId, "end")

  if (start && start !== defaultStart) {
    params.set(startKey, start)
  } else {
    params.delete(startKey)
  }

  if (end && end !== defaultEnd) {
    params.set(endKey, end)
  } else {
    params.delete(endKey)
  }

  replaceUrl(params)
}

export const writeCalendarUrlEmployeeIds = (
  organizationId: string | null | undefined,
  employeeIds: string | string[] | null | undefined,
) => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search || "")
  const employeeKey = key(organizationId, "employees")
  const normalized = normalizeEmployeeIds(employeeIds)

  if (normalized.length) {
    params.set(employeeKey, JSON.stringify(normalized))
  } else {
    params.delete(employeeKey)
  }

  replaceUrl(params)
}
